import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { DocumentFileEntityRow } from '@risksmart-app/trpc/src/types';
import type { GetLatestPublicDocumentFileByDocumentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

function mapTrpcDocumentToGraphQL(
  document: DocumentFileEntityRow
): GetLatestPublicDocumentFileByDocumentIdQuery['document_file'][0] {
  return { ...document };
}

/**
 * Maps TRPC document data to match the GraphQL query structure
 */
function mapTrpcDocumentToGraphQLQuery(
  trpcData: DocumentFileEntityRow | undefined
): GetLatestPublicDocumentFileByDocumentIdQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    document_file: [mapTrpcDocumentToGraphQL(trpcData)],
  };
}

export const useGetLatestPublicDocumentFileByDocumentId = (
  documentId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.documentFile.latestPublicDocumentFileByDocumentId.queryOptions(
      {
        documentId,
      }
    ),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcDocumentToGraphQLQuery(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
